import React from "react";
import { Card } from "./ui/card";
import { TrendingUp, AlertTriangle, Clock, CheckCircle2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const RISK_STYLES = {
  on_track: { label: "On track", chip: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2, dot: "bg-emerald-500" },
  watch:    { label: "Watch",    chip: "bg-amber-50 text-amber-700 border-amber-200",       icon: Clock,         dot: "bg-amber-500"   },
  at_risk:  { label: "At risk",  chip: "bg-rose-50 text-rose-700 border-rose-200",          icon: AlertTriangle, dot: "bg-rose-500"    },
  stalled:  { label: "Stalled",  chip: "bg-slate-200 text-slate-700 border-slate-300",      icon: AlertTriangle, dot: "bg-slate-500"   },
};

function shortDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return iso; }
}

function pluralDays(n) { return `${n} day${n === 1 ? "" : "s"}`; }

export default function CompletionForecastCard({ forecast }) {
  if (!forecast?.length) return null;

  const byRisk = forecast.reduce((acc, f) => {
    acc[f.risk] = (acc[f.risk] || 0) + 1;
    return acc;
  }, {});
  const order = ["stalled", "at_risk", "watch", "on_track"];

  return (
    <Card className="bg-white border-slate-200 p-5 mb-6 shadow-sm" data-testid="completion-forecast-card">
      <div className="flex items-center gap-2 mb-1">
        <TrendingUp size={16} className="text-brand-primary"/>
        <div className="font-medium text-brand-primary">Completion forecast</div>
        <span className="ml-1 text-xs text-slate-500">— predicted submission ETA for every in-progress onboarding</span>
      </div>
      <p className="text-xs text-slate-500 mb-4">Risk class is based on idle time + remaining fields + your typical client pace.</p>

      {/* Risk summary chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        {order.map(r => byRisk[r] ? (
          <span key={r} className={`px-2.5 py-1 rounded-full border text-xs font-medium ${RISK_STYLES[r].chip}`} data-testid={`forecast-summary-${r}`}>
            {RISK_STYLES[r].label}: {byRisk[r]}
          </span>
        ) : null)}
      </div>

      {/* Forecast rows */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
        {forecast.slice(0, 20).map(f => {
          const meta = RISK_STYLES[f.risk] || RISK_STYLES.watch;
          const Icon = meta.icon;
          return (
            <Link
              to={`/admin/onboarding/${f.id}`}
              key={f.id}
              className="flex items-center gap-3 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg p-3 transition group"
              data-testid={`forecast-row-${f.id}`}
            >
              <span className={`w-2 h-2 rounded-full ${meta.dot} shrink-0`}/>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-900 truncate">{f.company_name}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold border ${meta.chip} inline-flex items-center gap-1`}>
                    <Icon size={10}/>{meta.label}
                  </span>
                </div>
                {/* mini progress bar */}
                <div className="mt-1.5 flex items-center gap-2 text-xs text-slate-600">
                  <div className="flex-1 h-1.5 max-w-[200px] bg-slate-200 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${meta.dot}`} style={{ width: `${Math.max(2, f.progress_pct)}%` }}/>
                  </div>
                  <span className="font-semibold text-slate-700">{f.progress_pct}%</span>
                  <span className="text-slate-400">·</span>
                  <span>{f.remaining_fields} fields left</span>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-semibold text-slate-900">{shortDate(f.eta_date)}</div>
                <div className="text-[11px] text-slate-500">
                  {f.risk === "stalled"
                    ? `idle ${pluralDays(f.idle_days)}`
                    : f.idle_days >= 2
                      ? `idle ${pluralDays(f.idle_days)} · ETA ~${f.projected_calendar_days}d`
                      : `ETA ~${f.projected_calendar_days}d`}
                </div>
              </div>
              <ExternalLink size={14} className="text-slate-400 group-hover:text-brand-primary shrink-0 transition"/>
            </Link>
          );
        })}
      </div>
      {forecast.length > 20 && (
        <div className="text-xs text-slate-500 text-center pt-3">Showing top 20 of {forecast.length} — sorted by risk</div>
      )}
    </Card>
  );
}
